import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  TableSortLabel,
  Box,
  TextField,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
import moment from 'moment';
import LoadProgress from '../../common/LoadProgress.js';
import SystemAlert from '../../common/SystemAlert.js';
import axios from 'axios';
import { getInstructionLifecycleChipProps } from './instructionLifecycleChip';

export default function InstructionsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);
  const [instructions, setInstructions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [alert, setAlert] = React.useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');

  // Sorting state
  const [sortBy, setSortBy] = React.useState('updatedOn');
  const [sortDirection, setSortDirection] = React.useState('desc');

  // Handle delete click
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    try {
      await axios.post(`/instructions/delete?id=${deleteId}`);

      // Update state
      setInstructions(instructions.filter(instruction => instruction.id !== deleteId));
      setOpenDialog(false);
      setAlert({ severity: 'success', message: 'Instructions deleted successfully' });

      // Clear alert after 3 seconds
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      console.error('Error deleting instruction:', err);
      setAlert({ severity: 'error', message: 'Failed to delete instructions. This Instruction might be used in an Agent.' });
      setTimeout(() => setAlert(null), 3000);
    }
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteConfirmText('');
  };

  // Handle duplicate instruction
  const handleDuplicate = async (id) => {
    try {
      const response = await axios.post(`/instructions/duplicate?id=${id}`);

      // Add the duplicated instruction to the list
      setInstructions([...instructions, response.data]);
      setAlert({ severity: 'success', message: 'Instructions duplicated successfully' });

      // Clear alert after 3 seconds
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      console.error('Error duplicating instruction:', err);
      setAlert({ severity: 'error', message: 'Failed to duplicate instructions' });
      setTimeout(() => setAlert(null), 3000);
    }
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedInstructions = React.useMemo(() => {
    const sorted = [...instructions].sort((a, b) => {
      let aValue, bValue;
      if (sortBy === 'name') {
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
      } else if (sortBy === 'createdOn') {
        aValue = arrayToDate(a.dateCreated);
        bValue = arrayToDate(b.dateCreated);
      } else if (sortBy === 'updatedOn') {
        aValue = arrayToDate(a.dateUpdated);
        bValue = arrayToDate(b.dateUpdated);
      } else {
        aValue = '';
        bValue = '';
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [instructions, sortBy, sortDirection]);

  // Load instructions from API on component mount
  React.useEffect(() => {
    const fetchInstructions = async () => {
      try {
        setLoading(true);

        // Direct API call with full URL
        const response = await axios.post('/instructions/getAll');
        console.log('API response:', response);

        setInstructions(response.data);
        console.log('Instructions data:', JSON.stringify(response.data, null, 2));
        setError(null);
      } catch (err) {
        console.error('Error fetching instructions:', err);
        setError('Failed to load instructions. Please try again later.');
        setInstructions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructions();
  }, []);

  React.useEffect(() => {
    const incomingAlert = location?.state?.alert;
    if (!incomingAlert) {
      return;
    }

    setAlert(incomingAlert);
    const timerId = setTimeout(() => setAlert(null), 3000);

    navigate(location.pathname, { replace: true, state: {} });

    return () => {
      clearTimeout(timerId);
    };
  }, [location, navigate]);

  function arrayToDate(arr) {
    if (!Array.isArray(arr) || arr.length < 6) return null;
    return new Date(
      arr[0],           // year
      arr[1] - 1,       // month (0-based)
      arr[2],           // day
      arr[3],           // hour
      arr[4],           // minute
      arr[5],           // second
      arr[6] ? Math.floor(arr[6] / 1000000) : 0 // ms from nanoseconds if present
    );
  }

  const instructionToDelete = instructions.find(i => i.id === deleteId);

  return (
    <div>
      {
        alert && (
          <SystemAlert open={alert !== null} severity={alert.severity} message={alert.message} />
        )
      }

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px' }}>
        <Typography variant="h5" component="h1">
          Instructions
        </Typography>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Button
            variant='outlined'
            onClick={() => navigate('/InstructionsInfo')}
          >
            Add New Instructions
          </Button>
        </div>
      </div>

      {loading ? (
        <LoadProgress open={loading} />
      ) : error ? (
        <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
          {error}
        </div>
      ) : (
        <Table aria-label="instructions table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Instructions Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'createdOn'}
                  direction={sortBy === 'createdOn' ? sortDirection : 'asc'}
                  onClick={() => handleSort('createdOn')}
                >
                  Created On
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'updatedOn'}
                  direction={sortBy === 'updatedOn' ? sortDirection : 'asc'}
                  onClick={() => handleSort('updatedOn')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedInstructions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No instructions found</TableCell>
              </TableRow>
            ) : (
              sortedInstructions.map((instruction) => (
                (() => {
                  const lifecycleState = (instruction.lifecycleState || '').toUpperCase();
                  const isDraft = lifecycleState === 'DRAFT';
                  const primaryActionTitle = isDraft ? 'Continue your interview' : 'Edit';
                  const duplicateTitle = isDraft ? 'Draft duplication is not available yet' : 'Duplicate';

                  return (
                    <TableRow
                      key={instruction.id}
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                    >
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{instruction.name}</span>
                          {instruction.sourceType === 'ai_prompt_engineer' && (
                            <Chip label="Engineered By AI Instruction Architect" color="primary" size="small" />
                          )}
                          {instruction.lifecycleState && (
                            <Chip
                              {...getInstructionLifecycleChipProps(instruction.lifecycleState)}
                              size="small"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {instruction.createdBy || 'User'}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {instruction.dateCreated ? moment(arrayToDate(instruction.dateCreated)).format('MMMM Do, YYYY') : '-'}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {instruction.dateUpdated ? moment(arrayToDate(instruction.dateUpdated)).format('MMMM Do, YYYY') : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={primaryActionTitle}>
                          <IconButton
                            aria-label={primaryActionTitle}
                            size="small"
                            sx={{ cursor: 'pointer', color: 'success.main' }}
                            onClick={() => navigate(`/InstructionsInfo/${instruction.id}`)}
                          >
                            {isDraft ? <VideoSettingsIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={duplicateTitle}>
                          <span>
                            <IconButton
                              aria-label={duplicateTitle}
                              size="small"
                              sx={{ cursor: 'pointer', color: 'primary.main' }}
                              disabled={isDraft}
                              onClick={() => handleDuplicate(instruction.id)}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            aria-label="Delete"
                            size="small"
                            sx={{ cursor: 'pointer', color: 'error.main' }}
                            onClick={() => handleDeleteClick(instruction.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })()
              ))
            )}
          </TableBody>
        </Table>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete these instructions:
            <Box component="div" mt={1} mb={2} sx={{ pl: 2, fontWeight: 'bold' }}>
              {instructionToDelete ? instructionToDelete.name : ''}
            </Box>
            <Box component="div" mb={2}>
              This action cannot be undone.
            </Box>
            <Box component="div" mb={1}>
              Type <span style={{ color: 'red', fontWeight: 'bold' }}>Delete</span> (case sensitive) to confirm.
            </Box>
          </DialogContentText>
          <Box mt={2}>
            <TextField
              autoFocus
              size="small"
              label='Type "Delete" to confirm'
              fullWidth
              variant="outlined"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            variant='contained'
            disabled={deleteConfirmText !== 'Delete'}
          >
            Delete
          </Button>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}